import { ActionDataWidgets } from '@app/page/src/components/widgets/types/index'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  FLOWTAG_CONTACT_COLLECTION_DATA,
  NON_COMBINED_FUNNEL_CONTACT_WIDGETS,
  PDFTitle,
  SOCIAL_FORM_INPUTS,
  SocialFormInputType,
  VARIANT_2_EXCLUSIVE,
  WEBSITE_AND_SOCIAL_FORM_INPUT_ENTRIES
} from './SocialForm.constants'
import { FLOWTAG_DEFAULT_CRM } from '@app/code/src/constants/optimizely'
import { FlowtagPages } from '@dtx-company/true-common/src/types/flowtag'
import { Link, PageType } from '@dtx-company/flow-codegen/src/page/generated.types'
import { LinkType, PageType as TypedPage } from '@dtx-company/flow-codegen/src/page/typeUtils'
import { MaskType } from '@app/common/src/components/ImageEditor/ImageEditor.types'
import {
  SocialFormContactInfoType,
  SocialFormFields,
  SocialFormFieldsContactInfo,
  SocialFormFieldsHandles,
  SocialFormFieldsWidgets,
  SocialFormInputs,
  SocialFormProps,
  SocialFormState,
  SocialLinkMapType
} from './SocialForm.types'
import { addProtocol } from '@app/page/src/components/profile/LinkForm/utils'
import { buildActionData } from '@app/page/src/components/widgets/ContactCollectionForm/utils'
import { capitalize } from '@dtx-company/true-common/src/utils/strings'
import { defaultPageAttributes } from '@dtx-company/true-common/src/constants/page'
import { deleteLink } from '@dtx-company/inter-app/src/redux/slices/pageEditor/pageEditorSlice'
import { getCashAppUserName } from '@dtx-company/true-common/src/utils/flowpage'
import { getPDFTitleVariation, getWebsiteDescription } from './SocialForm.utils'
import { logoUrlForType } from '@app/page/src/utils/main'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useCodeIdFromRouter } from '@app/common/src/hooks/useCodeIdFromRouter'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useDispatch } from 'react-redux'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useForm } from 'react-hook-form-deprecated'
import { useSubmitWidget } from '@app/page/src/components/widgets/submitUtils'
import { useVariantFromRouter } from '@app/pre-build/src/hooks/useVariantFromRouter'
import { v4 as uuid } from 'uuid'
import { validateLink } from '@app/page/src/components/widgets/utils'
import events from '@dtx-company/inter-app/src/event-tracking/events/Flowtag'

export function useSocialForm({
  advanceStep,
  isFlowtagOnboarding = false,
  isCombinedFunnelMobile = false,
  hideFileInputs = false
}: SocialFormProps): SocialFormState {
  const [eventTriggered, setEventTriggered] = useState(false)
  const page = useCurrentPage()
  const dispatch = useDispatch()
  const submitWidget = useSubmitWidget()
  const { batchId } = useCodeIdFromRouter()

  const [_, { addFlowtagCRM }] = useFlowFeature(FLOWTAG_DEFAULT_CRM)
  const variant = useVariantFromRouter()
  const variantPDFTitle = getPDFTitleVariation(variant)

  const existingLinks = useMemo(() => page?.links ?? [], [page?.links])
  const existingWidgetLinks = useMemo(
    () => ({
      getBigImage: existingLinks.find(link => link?.type === 'bigImage'),
      getContactInformation: existingLinks.find(link => link?.type === 'contactInfo'),
      getPDF: existingLinks.find(link => link?.type === 'pdf')
    }),
    [existingLinks]
  )

  const existingWidgetValues = {
    [SocialFormFields.BIGIMAGE]: existingWidgetLinks.getBigImage?.thumbNailImgUrl ?? '',
    [SocialFormFields.CALL]: existingWidgetLinks.getContactInformation?.actionData?.call ?? '',
    [SocialFormFields.EMAIL]: existingWidgetLinks.getContactInformation?.actionData?.email ?? '',
    [SocialFormFields.SMS]: existingWidgetLinks.getContactInformation?.actionData?.text ?? '',
    [SocialFormFields.PDF]: existingWidgetLinks.getPDF?.actionData?.link ?? ''
  }

  const socialLinkMap = useMemo(
    () =>
      existingLinks.reduce<SocialLinkMapType>(
        (map, link) => {
          const { provider, type } = link

          const socialFormFieldName = provider === 'widget' ? type : provider
          const socialLinkFound =
            socialFormFieldName && socialFormFieldName.toUpperCase() in SocialFormFields

          if (link && socialLinkFound) {
            return {
              ...map,
              [socialFormFieldName]: link
            }
          }

          return map
        },
        {
          instagram: null,
          tiktok: null,
          snapchat: null,
          link: null,
          twitter: null,
          linkedin: null,
          venmo: null,
          cashapp: null,
          youtube: null,
          facebook: null,
          spotify: null,
          bigImage: null,
          email: null,
          sms: null,
          call: null,
          pdf: null
        }
      ),
    [existingLinks]
  )

  const contactCollectionLinkMap = existingLinks.reduce<{ widget: Link | null }>(
    (map, link) => {
      if (link?.provider && link.type === 'contactCollection') {
        return {
          ...map,
          [link.provider]: link
        }
      }
      return map
    },
    {
      widget: null
    }
  )

  const getSocialLinkData = (provider: SocialFormFields, link?: Link | null): string => {
    const data = link?.actionData?.link ?? ''

    if (data) {
      const prefix = SOCIAL_FORM_INPUTS[provider].prefix
      let clean = data.replace(prefix, '')

      if (provider === SocialFormFields.CASHAPP) clean = clean.replace('$', '')

      return clean
    }

    return data
  }

  const { register, setValue, handleSubmit, errors, watch, clearErrors, setError } = useForm({
    defaultValues: {
      instagram: getSocialLinkData(SocialFormFields.INSTAGRAM, socialLinkMap?.instagram),
      tiktok: getSocialLinkData(SocialFormFields.TIKTOK, socialLinkMap?.tiktok),
      snapchat: getSocialLinkData(SocialFormFields.SNAPCHAT, socialLinkMap?.snapchat),
      link: getSocialLinkData(SocialFormFields.LINK, socialLinkMap?.link),
      twitter: getSocialLinkData(SocialFormFields.TWITTER, socialLinkMap?.twitter),
      linkedin: getSocialLinkData(SocialFormFields.LINKEDIN, socialLinkMap?.linkedin),
      venmo: getSocialLinkData(SocialFormFields.VENMO, socialLinkMap?.venmo),
      cashapp: getSocialLinkData(SocialFormFields.CASHAPP, socialLinkMap?.cashapp),
      youtube: getSocialLinkData(SocialFormFields.YOUTUBE, socialLinkMap?.youtube),
      facebook: getSocialLinkData(SocialFormFields.FACEBOOK, socialLinkMap?.facebook),
      spotify: getSocialLinkData(SocialFormFields.SPOTIFY, socialLinkMap?.spotify),
      error: '',
      bigImage: existingWidgetValues[SocialFormFields.BIGIMAGE],
      email: existingWidgetValues[SocialFormFields.EMAIL],
      sms: existingWidgetValues[SocialFormFields.SMS],
      call: existingWidgetValues[SocialFormFields.CALL],
      pdf: existingWidgetValues[SocialFormFields.PDF]
    }
  })

  const onSubmit = async (
    finalValues: SocialFormInputs,
    sortedSocialInputList: [SocialFormFields, SocialFormInputType][]
  ): Promise<void> => {
    try {
      const hasInput = Object.values(finalValues).some(value => Boolean(value?.trim()?.length))
      if (!hasInput && !isFlowtagOnboarding)
        throw new Error(
          'there needs to be at least one non space character across all social link strings'
        )

      const toProcess = []
      const socialLinks: { provider: SocialFormFields; value: string; order: number }[] = []
      const bigImages: { provider: SocialFormFields; value: string; order: number }[] = []
      const contactInfos: { provider: SocialFormFields; value: string; order: number }[] = []
      const pdfFiles: { provider: SocialFormFields; value: string; order: number }[] = []

      sortedSocialInputList.forEach(([provider], index) => {
        const value = finalValues[provider]
        const linkObj = socialLinkMap?.[provider as SocialFormFields]
        const isWidget = SocialFormFieldsWidgets.includes(provider as SocialFormFields)
        const isContactInformation = SocialFormFieldsContactInfo.includes(
          provider as SocialFormFields
        )

        // if there is an existing link but the value was deleted then delete
        if (linkObj && !value?.length) {
          const { id, order } = linkObj
          dispatch(
            deleteLink({
              linkId: id,
              linkOrder: order
            })
          )
        } else if (isWidget) {
          const { getBigImage, getContactInformation, getPDF } = existingWidgetLinks

          let widgetObj = undefined
          switch (provider) {
            case SocialFormFields.BIGIMAGE:
              widgetObj = getBigImage
              break
            case SocialFormFields.EMAIL:
              widgetObj = getContactInformation
              break
            case SocialFormFields.SMS:
              widgetObj = getContactInformation
              break
            case SocialFormFields.CALL:
              widgetObj = getContactInformation
              break
            case SocialFormFields.PDF:
              widgetObj = getPDF
              break
          }

          // flowtag onboarding and non variant 2 will not have contact fields for social form
          // so don't delete in that case
          if (
            widgetObj &&
            (!isContactInformation || (isContactInformation && isCombinedFunnelMobile))
          ) {
            const { id, order } = widgetObj
            dispatch(
              deleteLink({
                linkId: id,
                linkOrder: order
              })
            )
          }
        }

        if (value?.length > 0) {
          const { widgetType } = SOCIAL_FORM_INPUTS[provider as SocialFormFields]
          const processObj = { provider, value, order: index }

          switch (widgetType) {
            case 'socialLink' as ActionDataWidgets:
              socialLinks.push(processObj)
              break
            case 'bigImage' as ActionDataWidgets:
              bigImages.push(processObj)
              break
            case 'pdf' as ActionDataWidgets:
              pdfFiles.push(processObj)
              break
            case 'contactInfo' as ActionDataWidgets:
              contactInfos.push(processObj)
              break
          }
        }
      })

      // validate and build submission calls

      if (socialLinks.length > 0)
        toProcess.push(
          socialLinks.map(
            async ({
              provider,
              value,
              order
            }: {
              provider: SocialFormFields
              value: string
              order: number
            }): Promise<void> => {
              const linkObj = socialLinkMap?.[provider] ?? undefined
              const linkID = linkObj?.id || uuid()
              const { prefix, widgetType, label, inputAdornmentText } = SOCIAL_FORM_INPUTS[provider]

              const isWebsite = provider === SocialFormFields.LINK
              const isCashApp = provider === SocialFormFields.CASHAPP
              const isHandle = SocialFormFieldsHandles.includes(provider)

              let link = ''
              let title = ''
              let description = ''

              if (isWebsite && typeof value === 'string') {
                link = addProtocol(value)

                title = 'Click Here!'

                description = getWebsiteDescription(link)
              } else if (isCashApp) {
                const formattedValue = getCashAppUserName(value)
                link = /(http(s?)):\/\//i.test(formattedValue.toLowerCase())
                  ? formattedValue
                  : prefix + formattedValue

                title = label

                description = value
              } else {
                link = /(http(s?)):\/\//i.test(value.toLowerCase()) ? value : prefix + value

                title = label

                if (isHandle) description = `${inputAdornmentText}${value}`
                else description = link
              }

              await submitWidget({
                curr: linkObj,
                actionData: {
                  link
                },
                widgetType,
                fields: {
                  id: linkID,
                  provider,
                  title,
                  description,
                  order,
                  displayType: 'default',
                  embed: false
                },
                image: logoUrlForType(provider)
              })
            }
          )
        )

      if (bigImages.length)
        toProcess.push(
          bigImages.map(
            async ({
              provider,
              value,
              order
            }: {
              provider: SocialFormFields
              value: string
              order: number
            }): Promise<void> => {
              const linkObj = socialLinkMap?.[provider] ?? undefined
              const linkID = linkObj?.id || uuid()
              const { prefix, widgetType } = SOCIAL_FORM_INPUTS[provider]

              const image = /(http(s?)):\/\//i.test(value.toLowerCase()) ? value : prefix + value

              await submitWidget({
                curr: undefined,
                image,
                actionData: {
                  maskType: MaskType.RECTANGLE
                },
                widgetType,
                fields: {
                  id: linkID,
                  order
                }
              })
            }
          )
        )

      if (pdfFiles.length)
        toProcess.push(
          pdfFiles.map(
            async ({
              provider,
              value,
              order
            }: {
              provider: SocialFormFields
              value: string
              order: number
            }): Promise<void> => {
              const linkObj = socialLinkMap?.[provider] ?? undefined
              const linkID = linkObj?.id || uuid()
              const { prefix, widgetType } = SOCIAL_FORM_INPUTS[provider]

              const link = /(http(s?)):\/\//i.test(value.toLowerCase()) ? value : prefix + value

              await submitWidget({
                curr: undefined,
                actionData: {
                  link
                },
                widgetType,
                fields: {
                  id: linkID,
                  title: variantPDFTitle,
                  description: '',
                  order
                }
              })
            }
          )
        )

      // manually remove isVariant2 / variant logic, leave refactor above intact
      if (isCombinedFunnelMobile && contactInfos.length) {
        const submitContactInfo = async (): Promise<void> => {
          const email = contactInfos.find(({ provider }) => provider === SocialFormFields.EMAIL)
          const sms = contactInfos.find(({ provider }) => provider === SocialFormFields.SMS)
          const call = contactInfos.find(({ provider }) => provider === SocialFormFields.CALL)

          const firstValidCI = email ?? sms ?? call
          const provider = firstValidCI?.provider

          if (provider) {
            const linkObj = socialLinkMap?.[provider] ?? undefined
            const id = linkObj?.id || uuid()

            const actionData = {
              call: call?.value ?? '',
              email: email?.value ?? '',
              text: sms?.value ?? '',
              card: true
            }

            await submitWidget({
              curr: linkObj,
              actionData,
              widgetType: 'contactInfo',
              fields: {
                title: 'Contact Info',
                description: '',
                id,
                // contact info isn't sortable so set -1
                order: -1,
                displayType: 'widget'
              }
            })
          }
        }
        toProcess.push(submitContactInfo())
      }

      //flowtag only
      if (isFlowtagOnboarding && addFlowtagCRM && !contactCollectionLinkMap?.widget) {
        const actionData = buildActionData(FLOWTAG_CONTACT_COLLECTION_DATA)
        const order = 999 //should always be at the bottom

        await submitWidget({
          curr: undefined,
          actionData,
          widgetType: 'contactCollection' as ActionDataWidgets,
          image: FLOWTAG_CONTACT_COLLECTION_DATA.thumbNailImgUrl,
          fields: {
            title: FLOWTAG_CONTACT_COLLECTION_DATA.title,
            description: FLOWTAG_CONTACT_COLLECTION_DATA.description,
            id: uuid(),
            order,
            displayType: FLOWTAG_CONTACT_COLLECTION_DATA.displayType
          }
        })
        events.Flowtag_Socials_Default_CRM_Added(batchId, page?.slugName)
      }

      await Promise.all(toProcess.flat())
      advanceStep()
    } catch (e) {
      setError('error', {
        message: e,
        type: 'error'
      })
      console.error(e)
      sendErrorNotification('Error saving your social links')
    }
  }

  const values = watch()

  const handleChange = (fieldName: SocialFormFields) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      const { value, files } = e.target
      const file = files?.[0]

      clearErrors(fieldName)
      if (
        (fieldName === SocialFormFields.BIGIMAGE && file) ||
        (fieldName === SocialFormFields.PDF && file)
      ) {
        if (file?.size && file?.size <= 10000000) setValue(fieldName, URL.createObjectURL(file))
      } else {
        clearErrors('error')
        clearErrors(fieldName)
        setValue(fieldName, value)
      }
      if (!eventTriggered && value) {
        isFlowtagOnboarding &&
          events.userTypedFlowtagFormInput(batchId, FlowtagPages.Socials, fieldName)
        setEventTriggered(true)
      }
    }
  }

  const socialInputList = useMemo(() => {
    const isFlowtagCollege = isFlowtagOnboarding && variantPDFTitle === PDFTitle.COLLEGE
    let inputList: [SocialFormFields, SocialFormInputType][] = WEBSITE_AND_SOCIAL_FORM_INPUT_ENTRIES
    // we need to read in the existing order props from existingLinks
    // and sort the configuration list to match

    if (Object.values(socialLinkMap).some(value => value !== null)) {
      inputList = [...WEBSITE_AND_SOCIAL_FORM_INPUT_ENTRIES].sort((a, b) => {
        const [aProvider] = a
        const [bProvider] = b

        const aLink = Object.values(socialLinkMap).find(
          link => link?.provider === aProvider || link?.type === aProvider
        )
        const bLink = Object.values(socialLinkMap).find(
          link => link?.provider === bProvider || link?.type === bProvider
        )

        const aOrder = aLink?.order
        const bOrder = bLink?.order

        if (aOrder === undefined) return 1
        if (bOrder === undefined) return -1

        return aOrder - bOrder
      })
    } else {
      if (isFlowtagCollege) {
        const PDFLinkIndex = inputList.findIndex(([provider]) => provider === SocialFormFields.PDF)
        if (PDFLinkIndex > 1) {
          inputList.splice(1, 0, inputList[PDFLinkIndex])
          inputList.splice(PDFLinkIndex + 1, 1)
        } else if (PDFLinkIndex === 0) {
          inputList.splice(0, 0, inputList[1])
        }
      }
    }
    // replace PDF input's label if the title needs to be submitted as college variation
    // keep as "file" otherwise
    if (isFlowtagCollege) {
      const PDFLinkIndex = inputList.findIndex(([provider]) => provider === SocialFormFields.PDF)
      inputList[PDFLinkIndex] = [
        SocialFormFields.PDF,
        { ...SOCIAL_FORM_INPUTS[SocialFormFields.PDF], ...{ label: variantPDFTitle } }
      ]
    }

    if (isCombinedFunnelMobile) {
      inputList = inputList.filter(
        ([provider]) => !NON_COMBINED_FUNNEL_CONTACT_WIDGETS.includes(provider)
      )
    } else {
      inputList = inputList.filter(([provider]) => !VARIANT_2_EXCLUSIVE.includes(provider))
    }

    if (hideFileInputs) {
      inputList = inputList.filter(
        ([provider]) => provider !== SocialFormFields.PDF && provider !== SocialFormFields.BIGIMAGE
      )
    }

    return inputList
  }, [isFlowtagOnboarding, socialLinkMap, variantPDFTitle, isCombinedFunnelMobile, hideFileInputs])

  useEffect(() => {
    register(SocialFormFields.INSTAGRAM)
    register(SocialFormFields.TIKTOK)
    register(SocialFormFields.SNAPCHAT)
    register(SocialFormFields.LINK, {
      validate: value => (value ? validateLink(value) : true)
    })
    register(SocialFormFields.TWITTER)
    register(SocialFormFields.LINKEDIN)
    register(SocialFormFields.VENMO)
    register(SocialFormFields.CASHAPP)
    register(SocialFormFields.YOUTUBE)
    register(SocialFormFields.FACEBOOK)
    register(SocialFormFields.SPOTIFY)
    register(SocialFormFields.BIGIMAGE)
    register(SocialFormFields.EMAIL)
    register(SocialFormFields.SMS)
    register(SocialFormFields.CALL)
    register(SocialFormFields.PDF)
  }, [register])

  return {
    handleSubmit,
    errors,
    setValue,
    page,
    values,
    handleChange,
    onSubmit,
    socialInputList
  }
}

export const useSocialFormDemoPage = (
  values: SocialFormInputs,
  socialInputList: [SocialFormFields, SocialFormInputType][]
): TypedPage => {
  const page = useCurrentPage()
  const variant = useVariantFromRouter()
  const variantPDFTitle = getPDFTitleVariation(variant)

  const existingLinks = page?.links ?? []
  const links: LinkType[] = []
  const contactActionData: SocialFormContactInfoType = {}

  socialInputList.forEach(([provider], index) => {
    const value = values[provider]
    if (!value?.length) return

    const { inputAdornmentText, widgetType } = SOCIAL_FORM_INPUTS[provider as SocialFormFields]
    const isWidget = SocialFormFieldsWidgets.includes(provider as SocialFormFields)

    const order = index

    const socialLink = {
      id: provider,
      link: value,
      active: true,
      embed: false,
      displayType: 'default',
      order,
      title: capitalize(provider),
      description: `${inputAdornmentText}${value}`,
      type: widgetType,
      provider,
      actionData: { link: value }
    }

    if (isWidget) {
      switch (widgetType) {
        case 'bigImage' as ActionDataWidgets:
          links.push({
            ...socialLink,
            ...{
              provider: 'widget',
              actionData: {
                maskType: 'rectangle'
              },
              thumbNailImgUrl: value
            }
          })
          break
        case 'pdf' as ActionDataWidgets:
          links.push({
            ...socialLink,
            ...{
              provider: 'widget',
              actionData: { link: value },
              title: variantPDFTitle,
              description: ''
            }
          })
          break
        case 'contactInfo' as ActionDataWidgets:
          if (provider === SocialFormFields.SMS)
            contactActionData['text' as SocialFormFields] = value
          else contactActionData[provider as SocialFormFields] = value
          break
      }
    } else {
      links.push(socialLink)
    }
  })

  if (Object.values(contactActionData).some(value => value.length))
    links.push({
      id: 'contactInfo',
      embed: false,
      displayType: 'default',
      active: true,
      order: -1,
      title: 'Contact Info',
      description: '',
      type: 'contactInfo',
      provider: 'widget',
      actionData: contactActionData
    })

  if (!links.some(link => link.id === 'contactInfo')) {
    const existingContactInfoLink = existingLinks.find(link => link.type === 'contactInfo')
    if (existingContactInfoLink) links.push(existingContactInfoLink)
  }

  return {
    ...defaultPageAttributes,
    id: '',
    pageType: PageType.Default,
    profileImage: page?.profileImage,
    displayName: page?.displayName,
    caption: page?.caption,
    links
  }
}
